import { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import withShadowRoot from 'Shared/utils/shadow_dom';
import initializeComponent from 'Shared/react-initialize';
import { useAsync, IfPending, IfFulfilled, IfRejected } from 'react-async';
import trackInPiwikAndGA from 'Shared/Analytics/CombinedGAPiwik';
import { WPNewsletter } from 'Shared/newsletter';
import { getRecentlySeenMap, updateEntry, resetEntry } from './recently-seen-banners';

const maxHeight = '500px';
const Placeholder = styled.div`
   height: ${props => props.sizes.default + 'px'};
   margin-bottom: ${props => props.sizes.space};
   margin-top: ${props => props.sizes.space};
   @media screen and (min-width: ${props => props.sizes.small_width + 'px'}) {
      height: ${props => props.sizes['small'] + 'px'};
   }
   @media screen and (min-width: ${props => props.sizes.intermediate_width + 'px'}) {
      height: ${props => props.sizes['intermediate'] + 'px'};
   }
`;

const RenderBanner = ({ html, space }) => {
   const ref = useRef();
   const [seen, setSeen] = useState(false);

   const intersectionCallback = entries => {
      const entry = entries[0];
      if (!seen && entry.isIntersecting) {
         updateRecentlySeenBanners(ref);
         setSeen(true);
      }
   };

   useEffect(() => {
      addGAtoLinks(ref);
      setupAnyNewsletters(ref);
      const observer = new IntersectionObserver(intersectionCallback, {
         threshold: 1.0,
      });
      if (ref.current) {
         observer.observe(ref.current);
      }
      return () => {
         observer.disconnect();
      };
   }, [ref.current, html]);

   return (
      <div
         ref={ref}
         style={{
            width: '100%',
            overflow: 'hidden',
            maxHeight: maxHeight,
            zIndex: 0,
            position: 'relative',
            marginTop: space,
            marginBottom: space,
         }}
         dangerouslySetInnerHTML={{ __html: html }}
      />
   );
};

const BannerWithShadowDom = withShadowRoot(RenderBanner);

const getHtmlFromApi = props => {
   const { type } = props;
   const currentUrl = new URL(window.location.href);
   const apiUrl = new URL('/campaigns', window.location.origin);

   // Add any existing query params
   for (let pair of currentUrl.searchParams.entries()) {
      apiUrl.searchParams.append(pair[0], pair[1]);
   }

   currentUrl.searchParams;

   apiUrl.searchParams.append('currentUrl', currentUrl.toString());
   apiUrl.searchParams.append('type', type);

   const recentlySeenBanners = getRecentlySeenBanners();
   if (recentlySeenBanners.length > 0) {
      apiUrl.searchParams.append('excludePostids', JSON.stringify(recentlySeenBanners));
   }

   const response = new Request.API_2_0(`${apiUrl.pathname}${apiUrl.search}`)
      .send()
      .then(response => {
         if (!response) {
            throw new Error('Failed to fetch HTML');
         }
         return response;
      });

   return response;
};

// Returns an array of postids seen [hideAfterViews] times or more and last seen within 30 days
const getRecentlySeenBanners = () => {
   const recentlySeenMap = getRecentlySeenMap();
   const recentlySeenBanners = [];
   const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
   const hideAfterViews = 5;

   for (const [postid, entry] of Object.entries(recentlySeenMap)) {
      const isRecent = entry.lastSeen > thirtyDaysAgo;
      if (entry.count >= hideAfterViews && isRecent) {
         recentlySeenBanners.push(postid);
      } else if (!isRecent) {
         resetEntry(postid);
      }
   }

   return recentlySeenBanners;
};

const updateRecentlySeenBanners = ref => {
   if (!ref.current) {
      return;
   }

   const postidNode = ref.current.querySelector('[data-postid]');
   const postid = postidNode?.dataset?.postid;

   if (!postid) {
      return;
   }

   const shouldUpdate = !postidNode.querySelector('.is-large');

   if (shouldUpdate) {
      updateEntry(postid);
   }
};

const setupAnyNewsletters = ref => {
   if (!ref.current) {
      return;
   }

   const titleNode = ref.current.querySelector('[data-banner-title]');
   const title = titleNode?.dataset?.bannerTitle || 'Banner';

   ref.current.querySelectorAll('.newsletter-sub').forEach(newsLetterEl => {
      new WPNewsletter(newsLetterEl, title);
   });
};

const addGAtoLinks = ref => {
   if (!ref.current) {
      return;
   }
   const links = Array.from(ref.current.querySelectorAll('a'));
   const titleNode = ref.current.querySelector('[data-banner-title]');
   const title = titleNode?.dataset?.bannerTitle;

   if (!title) {
      return;
   }

   links.forEach(link => {
      link.addEventListener('click', () => {
         trackInPiwikAndGA({
            eventCategory: 'Campaign Banner',
            eventAction: `Campaign Banner - ${title} - Click`,
         });
      });
   });
};

export const WPCampaignBanner = props => {
   const { sizes, type } = props;
   const wantsPlaceholder = props.hasCampaigns || false;
   const fallbackComponent = props.fallbackComponent || (() => null);
   const state = useAsync({ promiseFn: getHtmlFromApi, ...props });

   return (
      <div>
         <IfPending state={state}>
            {() => (wantsPlaceholder ? <Placeholder sizes={sizes} type={type} /> : null)}
         </IfPending>
         <IfRejected state={state}>{fallbackComponent}</IfRejected>
         <IfFulfilled state={state}>
            {html => <BannerWithShadowDom html={html} space={sizes.space} />}
         </IfFulfilled>
      </div>
   );
};

initializeComponent('WPCampaignBanner', WPCampaignBanner);
